import { DEVICE_BY_METROLOGY } from "../../services/shared/DeviceTypeService.service";
import { ChatAction, Intent } from "./chatbot.types";

/**
 * Returned when no intent pattern matches the user input.
 * The UNKNOWN_INTENT response placeholder — ChatbotService replaces it with a translated key.
 */
export const UNKNOWN_INTENT: Intent = {
  id: "unknown",
  patterns: [],
  action: ChatAction.ANSWER,
  // Placeholder: ChatbotService substitutes with the 'chatbot.unknown' i18n key at runtime.
  response: ""
};

/**
 * Multilingual intent registry for the internal chatbot.
 *
 * All patterns are tested against a normalised string (lowercased, NFD diacritics stripped).
 * Matching non-navigation intents use first-match order; navigation matches are collected and ranked.
 *
 * Languages covered: es (Spanish), en (English), ca (Catalan), fr (French),
 *                    it (Italian), pt (Portuguese).
 *
 * Payloads avoid blank shell routes; `principal` is a profile-aware guarded route.
 */
export const CHATBOT_INTENTS: readonly Intent[] = [
  // ── Greetings ───────────────────────────────────────────────────────────────
  {
    id: "greeting",
    patterns: [
      /\b(hola|buenos\s+dias|buenas\s+tardes|buenas\s+noches|buenas)\b/,
      /\b(saludos|bon\s+dia|bona\s+tarda|bona\s+nit|ola)\b/,
      /\b(hi|hello|hey|good\s+morning|good\s+afternoon|good\s+evening|greetings)\b/,
      /\b(bonjour|bonsoir|salut|coucou)\b/,
      /\b(ciao|buongiorno|buonasera|salve)\b/
    ],
    action: ChatAction.ANSWER,
    response: "chatbot.greeting",
    description: "Saluda al usuario y le da la bienvenida al asistente."
  },

  // Help
  {
    id: "help",
    patterns: [
      /\b(ayuda|ajuda|que\s+puedo\s+hacer|que\s+puedes\s+hacer|que\s+puc\s+fer|que\s+haces)\b/,
      /\b(que\s+fas|comandos|como\s+funciona|com\s+funciona|instrucciones|opciones|listado)\b/,
      /\b(help|what\s+can\s+you\s+do|what\s+do\s+you\s+do|commands)\b/,
      /\b(how\s+does\s+this\s+work|instructions|options|list)\b/,
      /\b(aide|qu.est.ce\s+que\s+tu\s+fais|commandes|comment\s+ca\s+marche|instructions|options)\b/,
      /\b(aiuto|cosa\s+puoi\s+fare|comandi|come\s+funziona|istruzioni|opzioni)\b/,
      /\b(ajuda|o\s+que\s+podes\s+fazer|comandos|como\s+funciona|instrucoes|opcoes)\b/
    ],
    action: ChatAction.ANSWER,
    response: "chatbot.help",
    description:
      "Muestra la lista de capacidades del asistente con ejemplos de instrucciones aceptadas."
  },

  // Context: Switch agrupation
  {
    id: "context-agrupation",
    patterns: [
      /\b(ver\s+agrupacion|ir\s+a\s+agrupacion|mostrar\s+agrupacion)\b/,
      /\b(cambiar\s+(a\s+)?agrupacion|seleccionar\s+agrupacion)\b/,
      /\b(ver\s+zona|ir\s+a\s+zona|mostrar\s+zona)\b/,
      /\b(cambiar\s+(a\s+)?zona|seleccionar\s+zona)\b/,
      /\b(go\s+to\s+agrupation|show\s+agrupation|view\s+agrupation)\b/,
      /\b(switch\s+(to\s+)?agrupation|select\s+agrupation)\b/,
      /\b(go\s+to\s+zone|show\s+zone|switch\s+(to\s+)?zone|view\s+zone)\b/,
      /\b(aller\s+[aà]\s+agrupation|voir\s+zone|passer\s+[aà]\s+zone|changer\s+de\s+zone)\b/,
      /\b(vai\s+(all[ao]\s+)?agrupazione|mostra\s+zona|cambia\s+(la\s+)?zona)\b/,
      /\b(ir\s+para\s+agrupacao|ver\s+zona|mudar\s+(de\s+)?zona)\b/,
      // Direct noun form (without verb prefix): "agrupacion Zona Central"
      /\bagrupaci[oó]n[es]*\s+\w/,
      /\bagrupations?\s+\w/,
      /\bagrupaments?\s+\w/,
      /\bgroupements?\s+\w/,
      /\bragruppament[io]\s+\w/
    ],
    action: ChatAction.SET_CONTEXT,
    contextKind: "agrupation",
    response: "",
    description: "Cambia la agrupación activa de la sesión buscando el nombre indicado."
  },

  // Context: Switch entity
  {
    id: "context-entity",
    patterns: [
      /\b(ver\s+entidad|ir\s+a\s+entidad|mostrar\s+entidad)\b/,
      /\b(cambiar\s+(a\s+)?entidad|seleccionar\s+entidad)\b/,
      /\b(go\s+to\s+entity|show\s+entity|view\s+entity)\b/,
      /\b(switch\s+(to\s+)?entity|select\s+entity)\b/,
      /\b(aller\s+[aà]\s+entite|voir\s+entite|passer\s+[aà]\s+entite|changer\s+d.entite)\b/,
      /\b(vai\s+all.entita|mostra\s+entita|cambia\s+(l.)?entita)\b/,
      /\b(ir\s+para\s+entidade|ver\s+entidade|mudar\s+(de\s+)?entidade)\b/,
      // Direct noun form (without verb prefix): "entidad MiEntidad"
      /\bentidad[es]*\s+\w/,
      /\bentit[yé]s?\s+\w/,
      /\bentitat?\s+\w/
    ],
    action: ChatAction.SET_CONTEXT,
    contextKind: "entity",
    response: "",
    description: "Cambia la entidad activa de la sesión buscando el nombre indicado."
  },

  // Context: Switch client
  {
    id: "context-client",
    patterns: [
      /\b(ver\s+cliente|ir\s+a\s+cliente|mostrar\s+cliente)\b/,
      /\b(cambiar\s+(a\s+)?cliente|seleccionar\s+cliente)\b/,
      /\b(go\s+to\s+client|show\s+client|view\s+client)\b/,
      /\b(switch\s+(to\s+)?client|select\s+client)\b/,
      /\b(aller\s+[aà]\s+client|voir\s+client|changer\s+de\s+client|selectionner\s+client)\b/,
      /\b(vai\s+al\s+cliente?|mostra\s+cliente?)\b/,
      /\bcambia\s+(il\s+)?cliente?\b/,
      /\b(ir\s+para\s+cliente|ver\s+cliente|mudar\s+(de\s+)?cliente)\b/,
      // Direct noun forms: "cliente MiCliente" or "client SomeCo"
      /\bclientes?\s+\w/,
      /\bclients?\s+\w/
    ],
    action: ChatAction.SET_CONTEXT,
    contextKind: "client",
    response: "",
    description: "Cambia el cliente activo de la sesión buscando el nombre indicado."
  },

  // ── Date-range detection ────────────────────────────────────────────────────
  // Full date parsing runs via detectDatePeriod() in chatbot-date.util.ts.
  // This entry adds an intent match for date-related requests.
  {
    id: "detect-date",
    patterns: [
      /\bultimos?\s+\d+\s+dias?\b/i,
      /\bultimos?\s+\d+\s+semanas?\b/i,
      /\bultimos?\s+\d+\s+meses?\b/i,
      /\bultima\s+semana\b/i,
      /\bultimo\s+mes\b/i,
      /\bultimo\s+a[nñ]o\b/i,
      /\beste\s+mes\b/i,
      /\b(last\s+\d+\s+days?|last\s+week|last\s+month|last\s+year)\b/i,
      /\b(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+\d{4}\b/i,
      /\b\d{1,2}[-/]\d{1,2}[-/]\d{4}\b/
    ],
    action: ChatAction.SET_DATE,
    response: "chatbot.date-set",
    description:
      "Detecta un rango de fechas en lenguaje natural (ej: «últimos 7 días», «enero 2024», «primer trimestre»). Compatible con español, inglés, catalán, francés e italiano."
  },

  // ── Query filter: leak detection (query 4) ──────────────────────────────────
  // Navigates to analisis-datos/consultas and pre-applies the leaks query filter.
  {
    id: "query-fugas",
    patterns: [
      /\bcon\s+fugas?\b/i,
      /\bfugas?\s+hidraulicas?\b/i,
      /\bdetectar\s+fugas?\b/i,
      /\bbuscar\s+fugas?\b/i,
      /\bwater\s+leaks?\b/i,
      /\bleak\s+detection\b/i,
      /\btienen?\s+fugas?\b/i,
      /\bhay\s+fugas?\b/i,
      // Bare noun form: "fugas" as the main subject (e.g. "Fugas con más de 15 litros").
      /\bfugas?\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis",
    description:
      "Navega a Consultas (query 4) y activa el filtro de fugas hidráulicas. Extrae automáticamente el umbral de consumo de la frase (ej: «más de 15 litros/hora» → consumo = 15). Sin fecha → aplica hoy por defecto."
  },

  // ── Query filter: no-communication meters (query 2) ─────────────────────────
  {
    id: "query-parados",
    patterns: [
      /\bcontadores?\s+parados?\b/i,
      /\bmedidores?\s+parados?\b/i,
      /\bsin\s+comunicacion\b/i,
      /\bmeters?\s+stopped\b/i,
      /\bwithout\s+communication\b/i,
      /\bno\s+signal\b/i,
      /\bse\s+han\s+parados?\b/i,
      /\bhan\s+parado\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis",
    description:
      "Navega a Consultas (query 2) y activa el filtro de contadores sin comunicación / parados. Extrae el rango de fechas si se indica (ej: «el último mes»). Sin fecha → aplica hoy por defecto."
  },

  // ── Query filter: negative consumption (query 3) ─────────────────────────────
  {
    id: "query-consumo-negativo",
    patterns: [
      /\bconsumo\s+negativo\b/i,
      /\bcontadores?\s+(?:con\s+)?consumo\s+negativo\b/i,
      /\bnegative\s+consumption\b/i,
      /\bconsommation\s+negative\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis",
    description:
      "Navega a Consultas (query 3) y activa el filtro de contadores con consumo negativo."
  },

  // ── Query filter: abnormal consumption peak (query 5) ────────────────────────
  {
    id: "query-picos-anomalos",
    patterns: [
      /\bpicos?\s+anomalos?\b/i,
      /\bcontadores?\s+(?:con\s+)?picos?\b/i,
      /\babnormal\s+(?:consumption\s+)?peaks?\b/i,
      /\bpic\s+anormal\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis",
    description: "Navega a Consultas (query 5) y activa el filtro de picos de consumo anómalos."
  },

  // ── Query filter: consumption comparison between two periods (query 9) ────────
  {
    id: "query-comparacion",
    patterns: [
      /\bcomparacion\s+(?:de\s+)?(?:consumos?|periodos?)\b/i,
      /\bcomparar\s+(?:consumos?|periodos?)\b/i,
      /\bcomparativa\s+(?:de\s+)?consumo\b/i,
      /\bcomparison\s+(?:of\s+)?consumption\b/i,
      /\bcompare\s+(?:consumption|periods?)\b/i,
      // Bare form: "comparativa" followed by periods indicator (e.g. "Comparativa dos períodos").
      /\bcomparativa\s+(?:\w+\s+)?periodos?\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis",
    description:
      "Navega a Consultas (query 9) y activa la comparativa de consumos entre dos períodos."
  },

  // ── Query filter: meters with initial values after installation (query 10) ────
  {
    id: "query-valores-iniciales",
    patterns: [
      /\bvalor(?:es)?\s+inicial(?:es)?\b/i,
      /\bcontadores?\s+instalados?\b/i,
      /\bcontadores?\s+(?:con\s+)?valor\s+inicial\b/i,
      /\binitial\s+(?:meter\s+)?values?\b/i,
      /\bvaleurs?\s+initiales?\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis",
    description:
      "Navega a Consultas (query 10) y activa el filtro de contadores instalados con valores iniciales en un rango de fechas."
  },

  // ── Query filter: meters by hourly consumption threshold (query 1) ────────────
  {
    id: "query-consumo-hora",
    patterns: [
      /\bcontadores?\s+por\s+consumo\b/i,
      /\bcontadores?\s+por\s+hora\b/i,
      /\bconsumo\s+por\s+hora\b/i,
      /\bmeters?\s+by\s+consumption\b/i,
      /\bhourly\s+consumption\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis",
    description:
      "Navega a Consultas (query 1) y activa el filtro de contadores por consumo horario superior a un umbral."
  },

  // ── Query filter: meters with no recent values / last-value date older than N days ───
  {
    id: "query-ultimo-valor",
    patterns: [
      /\bultimo\s+valor\b/i,
      /\bfecha\s+(?:de\s+)?ultimo\s+valor\b/i,
      /\bvalores?\s+no\s+recibidos?\b/i,
      /\bno\s+(?:tienen?|han)\s+(?:enviado|recibido)\s+(?:datos?|valores?)\b/i,
      /\bsin\s+valores?\s+recientes?\b/i,
      /\bsin\s+lecturas?\s+recientes?\b/i,
      /\bsin\s+valor\b/i,
      /\bcontadores?\s+sin\s+valor\b/i,
      /\blast\s+value\s+(?:date|older)\b/i,
      /\bno\s+recent\s+(?:values?|data|readings?)\b/i
    ],
    action: ChatAction.NAVIGATE,
    // Navigate to the assigned-devices screen and pre-apply a date column filter
    // on 'date-last-value' (lastCommunication) so only meters whose last received
    // value is older than the relativeDays threshold are shown.
    payload: "dispositivos/listado/asignados",
    response: "chatbot.navigate-dispositivos",
    tableFilter: {
      columnTitle: "date-last-value",
      operator: "<",
      relativeDays: 4
    },
    description:
      "Navega a Dispositivos en red y activa el filtro de columna «Fecha último valor» mostrando sólo los contadores cuyo último valor recibido es anterior a 4 días."
  },

  // ── Navigation: Dashboard subsections ───────────────────────────────────────

  // Home: entities tab
  {
    id: "navigate-home-entidades",
    patterns: [
      /\b(panel\s+(?:de\s+)?entidades?|dashboard\s+entidades?|inicio\s+entidades?)\b/,
      /\b(home\s+entities?|entities?\s+dashboard|entities?\s+panel)\b/,
      /\b(inici\s+entitats?|tableau\s+entites?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "principal-entidades",
    response: "chatbot.navigate-entidades"
  },

  // Home: global tab
  {
    id: "navigate-home-global",
    patterns: [
      /\b(panel\s+global|dashboard\s+global|inicio\s+global|vista\s+global(?:\s+principal)?)\b/,
      /\b(global\s+dashboard|global\s+panel|global\s+home)\b/,
      /\b(inici\s+global|tableau\s+global)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "principal-global",
    response: "chatbot.navigate-home"
  },

  // ── Navigation: Home / Dashboard ────────────────────────────────────────────
  {
    id: "navigate-home",
    patterns: [
      /\b(inicio|principal|panel\s+principal|pagina\s+principal|pagina\s+de\s+inicio|inici|pagina\s+de\s+inici)\b/,
      /\b(home|dashboard|main\s+panel|main\s+page|start\s+page)\b/,
      /\b(accueil|tableau\s+de\s+bord|page\s+d.accueil|page\s+principale)\b/,
      /\b(inizio|bacheca|pagina\s+principale|pannello\s+principale)\b/
    ],
    action: ChatAction.NAVIGATE,
    // 'principal' triggers MainRedirectGuard and redirects to the appropriate home per profile.
    payload: "principal",
    response: "chatbot.navigate-home",
    description:
      "Navega al panel principal (redirige según el perfil del usuario: agrupación, entidad o global)."
  },

  // ── Navigation: Devices subsections ─────────────────────────────────────────

  // Devices: not assigned
  {
    id: "navigate-dispositivos-no-asignados",
    patterns: [
      /\b(dispositivos?\s+no\s+asignados?|medidores?\s+no\s+asignados?|sin\s+asignar)\b/,
      /\b(unassigned\s+devices?|not\s+assigned\s+devices?|unassigned\s+meters?)\b/,
      /\b(dispositius?\s+no\s+assignats?|appareils?\s+non\s+assignes?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/no-asignados",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: deactivated
  {
    id: "navigate-dispositivos-desactivados",
    patterns: [
      /\b(dispositivos?\s+desactivados?|medidores?\s+desactivados?|equipos?\s+desactivados?|desactivados)\b/,
      /\b(deactivated\s+devices?|disabled\s+devices?|inactive\s+meters?)\b/,
      /\b(dispositifs?\s+desactives?|appareils?\s+inactifs?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/desactivados",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: client
  {
    id: "navigate-dispositivos-cliente",
    patterns: [
      /\b(dispositivos?|medidores?|contadores?)\s+(?:del?\s+)?cliente\b/,
      /\b(client\s+devices?|client\s+meters?)\b/,
      /\bdevices?\s+of\s+client\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/cliente",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: review
  {
    id: "navigate-dispositivos-revision",
    patterns: [
      /\b(dispositivos?|medidores?)\s+(?:en\s+)?revision\b/,
      /\ben\s+revision\b/,
      /\brevision\s+(?:de\s+)?dispositivos?\b/,
      /\bdevices?\s+(?:under\s+)?review\b/,
      /\bmeters?\s+(?:in\s+)?review\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/revision",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: substitutions pending
  {
    id: "navigate-dispositivos-sustituciones-pendientes",
    patterns: [
      /\b(sustituciones?\s+pendientes?|cambios?\s+(?:estan\s+)?pendientes?|reemplazos?\s+pendientes?)\b/,
      /\b(pending\s+(?:device\s+)?substitutions?|substitutions?\s+pending)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/sustituciones-pendientes",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: substitutions
  {
    id: "navigate-dispositivos-sustituciones",
    patterns: [
      /\bsustituciones?\s+(?:de\s+)?dispositivos?\b/,
      /\bdispositivos?\s+sustituidos?\b/,
      /\breemplazos?\s+(?:de\s+)?dispositivos?\b/,
      /\b(device\s+substitutions?|meter\s+replacements?)\b/,
      /\bhistorial\s+(?:de\s+)?sustituciones\b/,
      /\bsustitucions?\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/sustituciones",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: no communication
  {
    id: "navigate-dispositivos-sin-comunicacion",
    patterns: [
      /\b(sin\s+comunicacion|sin\s+senal)\b/,
      /\bdispositivos?\s+sin\s+comunicacion\b/,
      /\b(offline\s+devices?|devices?\s+without\s+communication|(?:dispositivos?|contadores?)\s+offline)\b/,
      /\bappareils?\s+sans\s+communication\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/sin-comunicacion",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: activation error
  {
    id: "navigate-dispositivos-error-activacion",
    patterns: [
      /\b(error|fallo)\s+(?:de\s+)?activacion\b/,
      /\bdispositivos?\s+(?:con\s+)?error\s+(?:de\s+)?activacion\b/,
      /\b(activation\s+error|failed\s+activation)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/error-activacion",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: low battery
  {
    id: "navigate-dispositivos-bateria-baja",
    patterns: [
      /\b(bateria\s+baja|poca\s+bateria|dispositivos?\s+(?:con\s+)?bateria\s+baja)\b/,
      /\b(low\s+battery|battery\s+low|devices?\s+(?:with\s+)?low\s+battery)\b/,
      /\b(batterie\s+faible|batteria\s+bassa|bateria\s+fraca)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/bateria-baja",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: internal (LoRa)
  {
    id: "navigate-dispositivos-interno",
    patterns: [
      /\bdispositivos?\s+internos?\b/,
      /\bred\s+interna(?:\s+lora)?(?:\s+(?:de\s+)?dispositivos?)?\b/,
      /\binternal\s+(?:lora\s+)?devices?\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/interno",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: GPRS
  {
    id: "navigate-dispositivos-gprs",
    patterns: [
      /\b(dispositivos?\s+gprs|gprs\s+dispositivos?|gprs\s+internos?)\b/,
      /\b(gprs\s+devices?|devices?\s+gprs)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/interno-gprs",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: walk-by
  {
    id: "navigate-dispositivos-walk-by",
    patterns: [
      /\b(walk[\s-]by|walk\s+by\s+(?:dispositivos?|lectura)|lectura\s+en\s+campo)\b/,
      /\b(walk\s+by\s+devices?|walk\s+by\s+reading|walkby)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/walk-by",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: unknown M-Bus
  {
    id: "navigate-dispositivos-mbus",
    patterns: [
      /\b(?:dispositivos?|contadores?|medidores?)\s+m-?bus(?:\s+(?:desconocidos?|sin\s+asignar))?\b/,
      /\b(?:desconocidos?\s+m-?bus|m-?bus\s+(?:desconocidos?|sin\s+asignar))\b/,
      /\b(unknown\s+m-?bus\s+devices?|m-?bus\s+unknown)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/desconocidos-mbus",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: ignored
  {
    id: "navigate-dispositivos-ignorados",
    patterns: [
      /\b(dispositivos?\s+(?:estan\s+)?ignorados?|medidores?\s+ignorados?|ignorados?\s+dispositivos?|ignorados)\b/,
      /\b(ignored\s+devices?|meters?\s+ignored)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/dispositivos-ignorados",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: new (add device)
  {
    id: "navigate-dispositivos-nuevo",
    patterns: [
      /\b(nuevo\s+dispositivo|anadir\s+dispositivo|agregar\s+dispositivo|crear\s+dispositivo|registrar\s+dispositivo)\b/,
      /\b(new\s+device|add\s+device|create\s+device|register\s+device)\b/,
      /\b(nouveau\s+appareil|ajouter\s+appareil|nuovo\s+dispositivo|adicionar\s+dispositivo)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/nuevo",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: SIM cards
  {
    id: "navigate-dispositivos-sim",
    patterns: [
      /\b(sim\s+(?:cards?|tarjetas?)|tarjetas?\s+sim|gestion\s+sim)\b/,
      /\b(sim\s+management|sim\s+cards?\s+devices?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/sim",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: import readings
  {
    id: "navigate-dispositivos-importar-lecturas",
    patterns: [
      /\b(importar\s+lecturas?|carga\s+(?:de\s+)?lecturas?|subir\s+lecturas?)\b/,
      /\b(import\s+readings?|upload\s+readings?|load\s+readings?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/importar/lecturas",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: import text file
  {
    id: "navigate-dispositivos-importar-texto",
    patterns: [
      /\b(?:importar|cargar)\s+(?:un\s+)?fichero\s+(?:de\s+)?texto\b/,
      /\b(importar\s+(?:fichero\s+)?texto|carga\s+texto\s+dispositivos?)\b/,
      /\b(import\s+text\s+(?:file\s+)?devices?|text\s+import\s+devices?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/importar/texto",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: import images
  {
    id: "navigate-dispositivos-importar-imagenes",
    patterns: [
      /\b(?:importar|subir|cargar)\s+imagenes?(?:\s+de\s+dispositivos?)?\b/,
      /\bcarga\s+(?:de\s+)?imagenes?\s+dispositivos?\b/,
      /\b(import\s+images?\s+devices?|upload\s+images?\s+devices?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/importar/imagenes",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: import data (generic)
  {
    id: "navigate-dispositivos-importar-datos",
    patterns: [
      /\b(?:importar|cargar)\s+datos?\s+(?:de\s+)?dispositivos?\b/,
      /\bcarga\s+(?:de\s+)?datos?\s+dispositivos?\b/,
      /\b(import|upload)\s+device\s+data\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/importar/datos",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: M-Bus primary key
  {
    id: "navigate-dispositivos-mbus-clave-primaria",
    patterns: [
      /\b(clave\s+primaria\s+m-?bus|m-?bus\s+clave\s+primaria|primary\s+key\s+m-?bus)\b/,
      /\b(m-?bus\s+primary\s+key|chiave\s+primaria\s+m-?bus)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/fabricantes-mbus/clave-primaria",
    response: "chatbot.navigate-dispositivos"
  },

  // Devices: M-Bus common key
  {
    id: "navigate-dispositivos-mbus-clave-comun",
    patterns: [
      /\b(clave\s+comun\s+m-?bus|m-?bus\s+clave\s+comun|common\s+key\s+m-?bus)\b/,
      /\b(m-?bus\s+common\s+key|chiave\s+comune\s+m-?bus)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/fabricantes-mbus/clave-comun",
    response: "chatbot.navigate-dispositivos"
  },

  // ── Devices: valve state list ───────────────────────────────────────────────
  // These intents target the assigned-device list and pre-apply a translated state filter.
  // Handles Spanish examples such as "Listado de válvulas <estado> en <entidad/agrupación>"
  // tableFilter uses i18nValue so the translated text matches the valveStateParsed column.

  // Valves: closed (valve-close → "Cerrada" / "Close")
  {
    id: "navigate-valvulas-cerradas",
    patterns: [
      /\bvalvulas?\s+cerradas?\b/i,
      /\bvalvulas?\s+en\s+estado\s+cerrad[ao]\b/i,
      /\blistado\s+(?:de\s+)?valvulas?\s+cerradas?\b/i,
      /\bclosed\s+valves?\b/i,
      /\bvalves?\s+(?:that\s+are\s+|in\s+state\s+)?closed\b/i,
      /\bvalvules?\s+fermees?\b/i,
      /\bvalvole\s+chiuse\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/asignados",
    payloadHistoryData: {
      data: [DEVICE_BY_METROLOGY.METER, DEVICE_BY_METROLOGY.VALVE]
    },
    response: "chatbot.navigate-dispositivos",
    tableFilter: {
      columnTitle: "valve-state",
      operator: "==",
      relativeDays: 0,
      i18nValue: "valve-close"
    },
    description:
      "Navega al listado de contadores y filtra la columna de estado de válvula por «Cerrada»."
  },

  // Valves: open (valve-open → "Abierta" / "Open")
  {
    id: "navigate-valvulas-abiertas",
    patterns: [
      /\bvalvulas?\s+abiertas?\b/i,
      /\bvalvulas?\s+en\s+estado\s+abierto\b/i,
      /\blistado\s+(?:de\s+)?valvulas?\s+abiertas?\b/i,
      /\bopen\s+valves?\b/i,
      /\bvalves?\s+(?:that\s+are\s+|in\s+state\s+)?open\b/i,
      /\bvalvules?\s+ouvertes?\b/i,
      /\bvalvole\s+aperte\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/asignados",
    payloadHistoryData: {
      data: [DEVICE_BY_METROLOGY.METER, DEVICE_BY_METROLOGY.VALVE]
    },
    response: "chatbot.navigate-dispositivos",
    tableFilter: {
      columnTitle: "valve-state",
      operator: "==",
      relativeDays: 0,
      i18nValue: "valve-open"
    },
    description:
      "Navega al listado de contadores y filtra la columna de estado de válvula por «Abierta»."
  },

  // Valves: pending close (valve-command-close-pending → "Pendiente de cierre" / "Pending closing")
  {
    id: "navigate-valvulas-pendiente-cierre",
    patterns: [
      /\bvalvulas?\s+(?:pendientes?\s+(?:de\s+)?)?cierre\b/i,
      /\bvalvulas?\s+pendientes?\s+cerrar\b/i,
      /\bvalves?\s+pending\s+clos(?:e|ing)\b/i,
      /\bvalvules?\s+en\s+attente\s+(?:de\s+)?fermeture\b/i,
      /\bvalvole\s+in\s+attesa\s+(?:di\s+)?chiusura\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/asignados",
    payloadHistoryData: {
      data: [DEVICE_BY_METROLOGY.METER, DEVICE_BY_METROLOGY.VALVE]
    },
    response: "chatbot.navigate-dispositivos",
    tableFilter: {
      columnTitle: "valve-state",
      operator: "==",
      relativeDays: 0,
      i18nValue: "valve-command-close-pending"
    },
    description:
      "Navega al listado de contadores y filtra por válvulas con comando de cierre pendiente."
  },

  // Valves: pending open (valve-command-open-pending → "Pendiente de apertura" / "Pending opening")
  {
    id: "navigate-valvulas-pendiente-apertura",
    patterns: [
      /\bvalvulas?\s+(?:pendientes?\s+(?:de\s+)?)?apertura\b/i,
      /\bvalvulas?\s+pendientes?\s+abrir\b/i,
      /\bvalves?\s+pending\s+open(?:ing)?\b/i,
      /\bvalvules?\s+en\s+attente\s+(?:d.)?ouverture\b/i,
      /\bvalvole\s+in\s+attesa\s+(?:di\s+)?apertura\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/asignados",
    payloadHistoryData: {
      data: [DEVICE_BY_METROLOGY.METER, DEVICE_BY_METROLOGY.VALVE]
    },
    response: "chatbot.navigate-dispositivos",
    tableFilter: {
      columnTitle: "valve-state",
      operator: "==",
      relativeDays: 0,
      i18nValue: "valve-command-open-pending"
    },
    description:
      "Navega al listado de contadores y filtra por válvulas con comando de apertura pendiente."
  },

  // Valves: general list (no state filter)
  {
    id: "navigate-valvulas",
    patterns: [
      /\blistado\s+(?:de\s+)?valvulas?\b/i,
      /\bvalvulas?\s+(?:listado|list)\b/i,
      /\bcontadores?\s+(?:con\s+)?valvulas?\b/i,
      /\bvalve\s+(?:list|meters?|devices?)\b/i,
      /\bmeters?\s+(?:with\s+)?valves?\b/i,
      /\bliste?\s+(?:des?\s+)?valvules?\b/i,
      /\blista\s+(?:di\s+)?valvole\b/i
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/asignados",
    response: "chatbot.navigate-dispositivos",
    description: "Navega al listado de contadores asignados para ver los dispositivos con válvula."
  },

  // ── Navigation: Devices ─────────────────────────────────────────────────
  {
    id: "navigate-dispositivos",
    patterns: [
      /\b(dispositivos?|medidores?|contadores?)\b/,
      /\b(equipos?\s+de\s+medida|dispositius?|comptadors?)\b/,
      /\b(devices?|meters?|measuring\s+equipment)\b/,
      /\b(dispositifs?|compteurs?|appareils?\s+de\s+mesure)\b/,
      /\b(dispositivi|contatori|misuratori)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "dispositivos/listado/asignados",
    response: "chatbot.navigate-dispositivos",
    description:
      "Navega al listado de dispositivos asignados. Usa frases como: «dispositivos», «contadores», «medidores»."
  },

  // ── Navigation: Gateways subsections ────────────────────────────────────────

  // Gateways: available
  {
    id: "navigate-gateways-disponibles",
    patterns: [
      /\b(gateways?\s+disponibles?|concentradores?\s+disponibles?|pasarelas?\s+disponibles?)\b/,
      /\b(available\s+gateways?|available\s+concentrators?)\b/,
      /\b(passerelles?\s+disponibles?|concentratori\s+disponibili)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gateways/listado/disponibles",
    response: "chatbot.navigate-gateways"
  },

  // Gateways: in stock
  {
    id: "navigate-gateways-en-stock",
    patterns: [
      /\bgateways?\s+(?:en\s+)?stock\b/,
      /\bconcentradores?\s+(?:en\s+)?stock\b/,
      /\b(stock\s+gateways?|gateways?\s+in\s+stock)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gateways/listado/en-stock",
    response: "chatbot.navigate-gateways"
  },

  // Gateways: broken
  {
    id: "navigate-gateways-averiados",
    patterns: [
      /\bgateways?\s+(?:averiados?|con\s+averia|rotos?)\b/,
      /\bconcentradores?\s+(?:averiados?|rotos?)\b/,
      /\b(?:cuales\s+)?estan\s+(?:rotos|averiados)\b/,
      /\b(broken|faulty)\s+gateways?\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gateways/listado/averiados",
    response: "chatbot.navigate-gateways"
  },

  // Gateways: repair
  {
    id: "navigate-gateways-reparacion",
    patterns: [
      /\b(gateways?\s+(?:en\s+)?reparacion|reparacion\s+(?:de\s+)?gateways?)\b/,
      /\b(?:gateways?|concentradores?)\s+(?:en\s+)?(?:reparacion|reparandose)\b/,
      /\bgateways?\s+(?:under\s+|in\s+)?repair\b/,
      /\brepair\s+gateways?\b/,
      /\b(gateways?\s+reparation|riparazione\s+gateways?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gateways/listado/reparacion",
    response: "chatbot.navigate-gateways"
  },

  // Gateways: AppEUI
  {
    id: "navigate-gateways-appeui",
    patterns: [
      /\b(appeui|app\s+eui|application\s+eui|gateways?\s+appeui)\b/,
      /\b(appeui\s+configuration|gateways?\s+app\s+eui)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gateways/appeui",
    response: "chatbot.navigate-gateways"
  },

  // ── Navigation: Gateways ─────────────────────────────────────────────────────
  {
    id: "navigate-gateways",
    patterns: [
      /\b(gateways?|base\s+station|concentradores?\s+de\s+radio)\b/,
      /\b(concentradores?|nodos?\s+de\s+red|pasarelas?)\b/,
      /\b(network\s+nodes?|base\s+stations?)\b/,
      /\b(passerelles?|concentrateurs?|noeuds?\s+de\s+reseau)\b/,
      /\b(concentratori|nodi\s+di\s+rete)\b/,
      /\b(concentradores?|nos\s+de\s+rede)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gateways/listado/asignados",
    response: "chatbot.navigate-gateways",
    description: "Navega al listado de gateways / concentradores asignados."
  },

  // ── Navigation: Alarms subsections ──────────────────────────────────────────

  // Alarms: agrupation configuration
  {
    id: "navigate-alarmas-config",
    patterns: [
      /\b(configurar\s+alarmas?|configuracion\s+(?:de\s+)?alarmas?|alarmas?\s+configuracion)\b/,
      /\b(alarm\s+(?:configuration|settings?|setup)|configure\s+alarms?)\b/,
      /\b(configuration\s+alarmes?|configurer\s+alarmes?|configurazione\s+allarmi)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "alarmas/agrupacion/configuracion",
    response: "chatbot.navigate-alarmas"
  },

  // Alarms: entity configuration
  {
    id: "navigate-alarmas-entidad-config",
    patterns: [
      /\balarmas?\s+(?:de\s+)?entidad\s+configuracion\b/,
      /\bconfiguracion\s+(?:de\s+)?alarmas?\s+(?:de\s+)?(?:la\s+)?entidad\b/,
      /\balarmas?\s+entidad\b/,
      /\bentity\s+alarm\s+(?:configuration|settings?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "alarmas/entidad/configuracion",
    response: "chatbot.navigate-alarmas"
  },

  // Alarms: global
  {
    id: "navigate-alarmas-global",
    patterns: [
      /\b(alarmas?\s+globales?|alertas?\s+globales?|alarmas?\s+de\s+todos)\b/,
      /\b(?:todas\s+las\s+)?alarmas\s+del\s+sistema\b/,
      /\b(global\s+alarms?|all\s+alarms?|system[\s-]wide\s+alarms?)\b/,
      /\b(alarmes?\s+globales?|allarmi\s+globali)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "alarmas/global/listado",
    response: "chatbot.navigate-alarmas"
  },

  // Alarms: custom/personalized
  {
    id: "navigate-alarmas-personalizadas",
    patterns: [
      /\b(alarmas?\s+personalizadas?|alertas?\s+personalizadas?|(?:alarmas?|alertas?)\s+custom)\b/,
      /\b(custom\s+alarms?|personalized\s+alarms?|custom\s+alerts?)\b/,
      /\b(alarmes?\s+personnalisees?|allarmi\s+personalizzati)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "alarmas/personalizadas/configuracion",
    response: "chatbot.navigate-alarmas"
  },

  // Alarms: devices
  {
    id: "navigate-alarmas-dispositivos",
    patterns: [
      /\b(?:alarmas?|alertas?|avisos?)\s+(?:de\s+|por\s+)?(?:dispositivos?|contador(?:es)?|medidor(?:es)?)\b/,
      /\b(device\s+alarms?|meter\s+alarms?)\b/,
      /\balarmes?\s+appareils?\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "alarmas/dispositivos",
    response: "chatbot.navigate-alarmas"
  },

  // ── Navigation: Alarms ──────────────────────────────────────────────────────
  {
    id: "navigate-alarmas",
    patterns: [
      /\b(alarmas?|alertas?|avisos?|incidencias?|alarmes?)\b/,
      /\b(alarms?|alerts?|notifications?|incidents?)\b/,
      /\b(alarmes?|alertes?|avis)\b/,
      /\b(allarmi|avvisi|notifiche|incidenti)\b/,
      /\b(alarmes?|alertas?|notificacoes?|incidentes?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "alarmas/agrupacion/listado",
    response: "chatbot.navigate-alarmas",
    description: "Navega al listado de alarmas de la agrupación activa."
  },
  {
    id: "navigate-clientes",
    patterns: [/\b(clientes?|clients?)\b/, /\b(customers?)\b/, /\b(clienti)\b/],
    action: ChatAction.NAVIGATE,
    payload: "clientes/listado",
    response: "chatbot.navigate-clientes"
  },

  // ── Navigation: Users subsections ───────────────────────────────────────────

  // Users: global log
  {
    id: "navigate-usuarios-log",
    patterns: [
      /\blog\s+(?:de\s+)?usuarios?\s+global\b/,
      /\bhistorial\s+usuarios?\s+global\b/,
      /\bauditoria\s+usuarios?\b/,
      /\blog\s+(?:de\s+)?usuarios?$/,
      /\bquien\s+ha\s+(?:accedido|entrado)\b/,
      /\bglobal\s+user\s+(?:log|history)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "usuarios/log/global",
    response: "chatbot.navigate-usuarios"
  },

  // Users: agrupation log
  {
    id: "navigate-usuarios-log-agrupacion",
    patterns: [
      /\b(log\s+(?:de\s+)?usuarios?\s+agrupacion|historial\s+usuarios?\s+agrupacion)\b/,
      /\b(?:historial|log)\s+(?:de\s+)?accesos?\s+(?:por|de\s+la)\s+(?:zona|agrupacion)\b/,
      /\b(agrupation\s+user\s+(?:log|history)|user\s+log\s+agrupation)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "usuarios/log/agrupacion",
    response: "chatbot.navigate-usuarios"
  },

  // Users: import
  {
    id: "navigate-usuarios-importar",
    patterns: [
      /\b(importar\s+usuarios?|carga\s+(?:de\s+)?usuarios?|subir\s+usuarios?)\b/,
      /\b(?:importar|cargar|subir)\s+(?:la\s+)?lista\s+de\s+usuarios?\b/,
      /\b(import\s+users?|upload\s+users?|user\s+import)\b/,
      /\b(importer\s+utilisateurs?|importare\s+utenti)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "usuarios/importar",
    response: "chatbot.navigate-usuarios"
  },

  // ── Navigation: Users ─────────────────────────────────────────────────────
  {
    id: "navigate-usuarios",
    patterns: [
      /\b(usuarios?|usuaris?|cuentas?\s+de\s+usuario|administrar\s+usuarios?)\b/,
      /\b(users?|user\s+accounts?|manage\s+users?)\b/,
      /\b(utilisateurs?|comptes?\s+utilisateur)\b/,
      /\b(utenti|account\s+utente)\b/,
      /\b(utilizadores?|gerir\s+utilizadores?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "usuarios/listado",
    response: "chatbot.navigate-usuarios"
  },

  // ── Navigation: Analysis subsections ────────────────────────────────────────

  // Analysis: balance
  {
    id: "navigate-analisis-balance",
    patterns: [
      /\b(balance\s+(?:de\s+)?consumo|balance\s+energetico|balance\s+(?:de\s+)?lecturas?)\b/,
      /\b(consumption\s+balance|energy\s+balance|balance\s+analysis)\b/,
      /\b(bilan\s+(?:de\s+)?consommation|bilancio\s+energetico)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/balance",
    response: "chatbot.navigate-analisis"
  },

  // Analysis: evolution/consumption trend
  {
    id: "navigate-analisis-evolucion",
    patterns: [
      /\b(evolucion\s+(?:de\s+)?consumo|tendencia\s+(?:de\s+)?consumo|curva\s+evolucion)\b/,
      /\b(consumption\s+(?:trend|evolution)|usage\s+trend|energy\s+trend)\b/,
      /\b(evolution\s+(?:de\s+la\s+)?consommation|evoluzione\s+consumo)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/evolucion-consumo",
    response: "chatbot.navigate-analisis"
  },

  // Analysis: comparative graphs
  {
    id: "navigate-analisis-comparativa",
    patterns: [
      /\b(comparativa\s+(?:de\s+)?graficas?|comparar\s+graficas?)\b/,
      /\bgraficas?\s+comparativas?\b/,
      /\bcomparative\s+(?:graphs?|charts?)\b/,
      /\bcompare\s+(?:graphs?|charts?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/comparativa-graficas",
    response: "chatbot.navigate-analisis"
  },

  // Analysis: queries (+ leak / filter detection)
  {
    id: "navigate-analisis-consultas",
    patterns: [
      /\bconsultas?\s+(?:de\s+)?datos?\b/,
      /\b(consultar\s+datos?|consultas?\s+analisis)\b/,
      // Query-specific matches target this child route rather than the generic analysis route.
      /\b(contadores?|medidores?)\s+con\s+fugas?\b/,
      /\bfugas?\s+(?:de\s+)?(?:agua\s+)?(?:en\s+)?\w/,
      /\bfugas?\s+de\s+agua\b/i,
      /\bdeteccion\s+(?:de\s+)?fugas?\b/,
      /\bconsultar\s+(?:contadores?|medidores?)\b/,
      /\b(water\s+leaks?|meters?\s+with\s+leaks?)\b/,
      /\b(leak\s+detection|suspected\s+leaks?)\b/,
      // Generic query patterns
      /\b(data\s+queries?|query\s+data|meter\s+(?:data\s+)?query)\b/,
      /\brequetes?\s+donnees?\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/consultas",
    response: "chatbot.navigate-analisis"
  },

  // Analysis: advanced analytics
  {
    id: "navigate-analisis-avanzada",
    patterns: [
      /\b(analitica\s+avanzada|analisis\s+avanzado|analytics\s+avanzados?)\b/,
      /\b(advanced\s+analytics?|advanced\s+analysis)\b/,
      /\b(analytique\s+avancee|analisi\s+avanzata|analitica\s+avancada)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/analitica-avanzada",
    response: "chatbot.navigate-analisis"
  },

  // Analysis: associations
  {
    id: "navigate-analisis-asociaciones",
    patterns: [
      /\b(asociaciones?\s+(?:de\s+)?analisis|analisis\s+(?:de\s+)?asociaciones?|asociaciones)\b/,
      /\b(associations?\s+analysis|analysis\s+associations?)\b/,
      /\b(associations?\s+analyse|associazioni\s+analisi)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/asociaciones",
    response: "chatbot.navigate-analisis"
  },

  // ── Navigation: Data analysis ───────────────────────────────────────────
  {
    id: "navigate-analisis",
    patterns: [
      /\b(analisis|graficas?|graficos?|consumos?|informes?)\b/,
      /\b(reportes?|estadisticas?|lecturas?|curvas?)\b/,
      /\b(analysis|analytics|graphs?|charts?|consumption|reports?|statistics?|readings?)\b/,
      /\b(analyse|graphiques?|consommation|rapports?|statistiques?)\b/,
      /\b(analisi|grafici|consumo|report|statistiche)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "analisis-datos/grafica",
    response: "chatbot.navigate-analisis",
    description:
      "Navega a la gráfica de análisis de consumos. Funciona también para sub-secciones: balance, evolución, comparativa, consultas avanzadas."
  },

  // ── Navigation: Coverage subsections ────────────────────────────────────────

  // Coverage: M-Bus map
  {
    id: "navigate-cobertura-mbus",
    patterns: [
      /\b(cobertura\s+m-?bus|mapa\s+(?:(?:de\s+)?senal\s+)?m-?bus|m-?bus\s+coverage|m-?bus\s+map)\b/,
      /\b(m-?bus\s+cobertura|m-?bus\s+mapa|m-?bus\s+signal\s+map)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "cobertura/mapa-mbus",
    response: "chatbot.navigate-cobertura"
  },

  // ── Navigation: Coverage ────────────────────────────────────────────────────
  {
    id: "navigate-cobertura",
    patterns: [
      /\b(cobertura|senal|red\s+lora|topografia|rssi|cobertura\s+de\s+red)\b/,
      /\b(coverage|signal|lora\s+network|network\s+coverage|rssi)\b/,
      /\b(couverture|signal|reseau\s+lora|topographie)\b/,
      /\b(copertura|segnale|rete\s+lora)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "cobertura/test",
    response: "chatbot.navigate-cobertura",
    description:
      "Navega a la pantalla de cobertura / señal de red LoRa. Reconoce: «cobertura», «señal», «RSSI», «red LoRa»."
  },
  {
    id: "navigate-mapa",
    patterns: [
      /^\s*mapa\s*$/,
      /\b(ver\s+mapa(?:\s+de\s+dispositivos?)?|mapa\s+de\s+dispositivos?)\b/,
      /\bmapa\s+de\s+cobertura\b/,
      /^\s*map\s*$|\b(view\s+map|device\s+map)\b/,
      /\b(carte\b|voir\s+la\s+carte)\b/,
      /\b(mappa\b|visualizza\s+mappa)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "cobertura/mapa",
    response: "chatbot.navigate-mapa",
    description: "Navega al mapa de dispositivos con geolocalización."
  },

  // ── Navigation: Maintenance subsections ─────────────────────────────────────

  // Maintenance: global search
  {
    id: "navigate-mantenimiento-busqueda",
    patterns: [
      /\b(busqueda\s+(?:global\s+)?mantenimiento|mantenimiento\s+busqueda\s+global|buscar\s+(?:en\s+)?mantenimiento)\b/,
      /\b(maintenance\s+(?:global\s+)?search|search\s+(?:in\s+)?maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/busqueda-global",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: activations
  {
    id: "navigate-mantenimiento-activaciones",
    patterns: [
      /\b(activaciones?\s+(?:mantenimiento|global)|mantenimiento\s+activaciones?|ver\s+activaciones)\b/,
      /\b(maintenance\s+activations?|activations?\s+maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/activaciones",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: communication status
  {
    id: "navigate-mantenimiento-estado-comunicaciones",
    patterns: [
      /\bestado\s+(?:de\s+)?(?:las?\s+)?comunicacion(?:es)?\b/,
      /\bcomo\s+esta(?:n)?\s+las?\s+comunicacion(?:es)?\b/,
      /\b(comunicaciones?\s+estado|estado\s+comunicaciones?\s+mantenimiento)\b/,
      /\bcommunication\s+(?:status|state)\b/,
      /\betat\s+des\s+communications?\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/estado-comunicaciones",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: global alarms
  {
    id: "navigate-mantenimiento-alarmas-globales",
    patterns: [
      /\b(alarmas?\s+globales?\s+mantenimiento|mantenimiento\s+(?:de\s+)?alarmas?(?:\s+globales?)?)\b/,
      /\balarmas?\s+(?:de\s+)?mantenimiento\b/,
      /\b(global\s+maintenance\s+alarms?|maintenance\s+global\s+alarms?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/alarmas",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: concentrators
  {
    id: "navigate-mantenimiento-concentradores",
    patterns: [
      /\b(concentradores?\s+mantenimiento|mantenimiento\s+concentradores?)\b/,
      /\b(maintenance\s+concentrators?|concentrators?\s+maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/concentradores",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: battery status
  {
    id: "navigate-mantenimiento-bateria",
    patterns: [
      /\b(bateria\s+mantenimiento|mantenimiento\s+bateria|estado\s+bateria\s+global)\b/,
      /\b(maintenance\s+battery\s+status|battery\s+maintenance\s+report)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/bateria",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: multiple CUPS devices
  {
    id: "navigate-mantenimiento-cups-multiples",
    patterns: [
      /\bcups\s+multiples?\b/,
      /\bmultiples?\s+dispositivos?\s+cups\b/,
      /\bcups\s+con\s+multiples?\s+dispositivos?\b/,
      /\b(?:contadores?|dispositivos?|medidores?)\s+con\s+(?:varios|multiples?)\s+cups\b/,
      /\bmultiple\s+(?:devices?\s+)?cups\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/cups-multiples-dispositivos",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: incidents
  {
    id: "navigate-mantenimiento-incidencias",
    patterns: [
      /\b(incidencias?\s+(?:de\s+)?mantenimiento|mantenimiento\s+incidencias?|reporte\s+(?:de\s+)?incidencias?)\b/,
      /\b(maintenance\s+incidents?|incidents?\s+maintenance|maintenance\s+issues?)\b/,
      /\b(incidents?\s+maintenance|incidences?\s+maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/incidencias",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: command queue
  {
    id: "navigate-mantenimiento-cola-comandos",
    patterns: [
      /\b(cola\s+(?:de\s+)?comandos?|comandos?\s+cola|comandos?\s+pendientes?)\b/,
      /\b(command\s+queue|queue\s+commands?|pending\s+commands?)\b/,
      /\b(file\s+(?:de\s+)?commandes?|coda\s+comandi)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/cola-comandos",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: global users
  {
    id: "navigate-mantenimiento-usuarios-globales",
    patterns: [
      /\b(usuarios?\s+globales?(?:\s+mantenimiento)?|mantenimiento\s+usuarios?\s+globales?)\b/,
      /\b(global\s+users?\s+maintenance|maintenance\s+global\s+users?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/usuarios",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: client users
  {
    id: "navigate-mantenimiento-usuarios-cliente",
    patterns: [
      /\busuarios?\s+(?:del?\s+)?cliente(?:\s+mantenimiento)?\b/,
      /\bmantenimiento\s+usuarios?\s+cliente\b/,
      /\bclient\s+users?\s+maintenance\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/usuarios-cliente",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: user events
  {
    id: "navigate-mantenimiento-eventos-usuario",
    patterns: [
      /\beventos?\s+(?:de\s+)?usuario\b/,
      /\b(log|historial)\s+(?:de\s+)?eventos?(?:\s+(?:de\s+)?usuario)?\b/,
      /\buser\s+events?\b/,
      /\bevenements?\s+utilisateur\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/eventos-usuario",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: communication report
  {
    id: "navigate-mantenimiento-reporte-comunicaciones",
    patterns: [
      /\b(reporte\s+(?:de\s+)?comunicaciones?|informe\s+(?:de\s+)?comunicaciones?)\b/,
      /\b(communication\s+report|communications?\s+report)\b/,
      /\b(rapport\s+communications?|rapporto\s+comunicazioni)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/reporte-comunicaciones",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: LNS (Network Server)
  {
    id: "navigate-mantenimiento-lns",
    patterns: [
      /\b(lns|servidor\s+(?:de\s+)?red\s+lns|network\s+server\s+lns|configuracion\s+lns)\b/,
      /\b(lns\s+server|lns\s+configuration|lns\s+management)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/lns",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: gateway tasks
  {
    id: "navigate-mantenimiento-tareas",
    patterns: [
      /\b(tareas?\s+gateways?|gateways?\s+tareas?|programacion\s+gateways?)\b/,
      /\b(gateway\s+tasks?|tasks?\s+gateways?|scheduled\s+gateway\s+tasks?)\b/,
      /\b(taches?\s+gateways?|task\s+gateway)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/tareas-gateways",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: logs/registers
  {
    id: "navigate-mantenimiento-registros",
    patterns: [
      /\b(registros?\s+mantenimiento|mantenimiento\s+registros?|logs?\s+mantenimiento|logs?\s+del\s+sistema)\b/,
      /\bmaintenance\s+(?:logs?|registers?)\b/,
      /\b(?:logs?|registers?)\s+maintenance\b/,
      /\b(journaux?\s+maintenance|registri\s+manutenzione)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/registros",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: network construction
  {
    id: "navigate-mantenimiento-construccion-red",
    patterns: [
      /\b(construccion\s+(?:de\s+)?red|despliegue\s+(?:de\s+)?red|planificacion\s+(?:de\s+)?(?:la\s+)?red)\b/,
      /\b(network\s+construction|network\s+deployment|network\s+planning)\b/,
      /\b(construction\s+reseau|costruzione\s+rete)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/construccion-de-red",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: entity gateways (client perspective)
  {
    id: "navigate-mantenimiento-entidad-gateways-cliente",
    patterns: [
      /\b(gateways?\s+(?:del?\s+)?cliente(?:\s+entidad)?|mantenimiento\s+gateways?\s+cliente)\b/,
      /\b(client\s+gateways?\s+maintenance|gateways?\s+client\s+entity\s+maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/entidad/gateways-cliente",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: entity gateways
  {
    id: "navigate-mantenimiento-entidad-gateways",
    patterns: [
      /\b(gateways?\s+(?:de\s+(?:la\s+)?)?entidad(?:\s+mantenimiento)?|mantenimiento\s+gateways?\s+entidad)\b/,
      /\b(entity\s+gateways?\s+maintenance|gateways?\s+entity\s+maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/entidad/gateways",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: entity concentrators
  {
    id: "navigate-mantenimiento-entidad-concentradores",
    patterns: [
      /\b(concentradores?\s+(?:de\s+(?:la\s+)?)?entidad(?:\s+mantenimiento)?|mantenimiento\s+concentradores?\s+entidad)\b/,
      /\b(entity\s+concentrators?\s+maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/entidad/concentradores",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: edit agrupations
  {
    id: "navigate-mantenimiento-editar-agrupaciones",
    patterns: [
      /\b(editar\s+agrupaciones?\s+mantenimiento|mantenimiento\s+editar\s+agrupaciones?)\b/,
      /\b(?:editar|gestionar|administrar)\s+(?:las\s+)?agrupaciones\b/,
      /\b(edit\s+agrupations?\s+maintenance|manage\s+agrupations?\s+maintenance)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/entidad/editar-agrupaciones",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: optimize network
  {
    id: "navigate-mantenimiento-optimizar-red",
    patterns: [
      /\boptimizar\s+(?:la\s+)?red\b/,
      /\b(optimizacion|mejora)\s+(?:de\s+)?red\b/,
      /\bmejorar\s+(?:la\s+)?red\b/,
      /\b(optimize\s+network|network\s+optimization|optimise\s+network)\b/,
      /\b(optimiser\s+(?:le\s+)?reseau|ottimizzare\s+(?:la\s+)?rete)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/entidad/optimizar-red",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: data deletion
  {
    id: "navigate-mantenimiento-borrado-datos",
    patterns: [
      /\b(borrado|eliminacion)\s+(?:de\s+)?datos?\b/,
      /\b(?:borrar|eliminar)\s+(?:los\s+)?datos?\b/,
      /\b(data\s+deletion|delete\s+data|data\s+cleanup|erase\s+data)\b/,
      /\b(suppression\s+(?:de\s+)?donnees?|cancellazione\s+dati)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/entidad/borrado-datos",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: monitoring/fiscalizer
  {
    id: "navigate-mantenimiento-monitorizacion",
    patterns: [
      /\b(monitorizacion(?:\s+mantenimiento)?|fiscalizador|monitor\s+mantenimiento)\b/,
      /\b(monitoring\s+maintenance|maintenance\s+monitoring|fiscalizer)\b/,
      /\b(surveillance\s+maintenance|monitoraggio\s+manutenzione)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/monitorizacion/fiscalizador-mostrar",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: manufacturers
  {
    id: "navigate-mantenimiento-fabricantes",
    patterns: [
      /\b(fabricantes(?:\s+mantenimiento)?|mantenimiento\s+fabricantes?|configurar\s+fabricantes?)\b/,
      /\b(manufacturers?\s+maintenance|maintenance\s+manufacturers?|configure\s+manufacturers?)\b/,
      /\b(fabricants?\s+maintenance|produttori\s+manutenzione)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/configurar/fabricantes",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: cron / scheduled tasks
  {
    id: "navigate-mantenimiento-cron",
    patterns: [
      /\b(cron|tareas?\s+programadas?|scheduler|tarea\s+cron)\b/,
      /\b(cron\s+(?:jobs?|tasks?)|scheduled\s+tasks?|task\s+scheduler)\b/,
      /\b(taches?\s+programmees?|attivita\s+programmate)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/configurar/cron",
    response: "chatbot.navigate-mantenimiento"
  },

  // Maintenance: IP configuration
  {
    id: "navigate-mantenimiento-ip",
    patterns: [
      /\b(configurar\s+ip\s+mantenimiento|ip\s+mantenimiento|mantenimiento\s+(?:configuracion\s+)?ip|configuracion\s+(?:de\s+)?ip)\b/,
      /\b(ip\s+configuration\s+maintenance|maintenance\s+ip\s+configuration)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/configurar/ip",
    response: "chatbot.navigate-mantenimiento"
  },

  // ── Maintenance: Global model search ─────────────────────────────────────
  // Specific patterns and hierarchical intent scoring distinguish it from generic maintenance navigation.
  // Handles Spanish examples such as "cuántos contadores hay <modelo> en <entidad/agrupación>?"
  // Explicit entity/agrupation terms are resolved and applied before model-search navigation.
  {
    id: "navigate-mantenimiento-busqueda-por-modelo",
    patterns: [
      /\bcuantos?\s+(?:contadores?\s+)?(?:\w+\s+){0,3}(?:hay|existen?|tienen?)\b/i,
      /\bcuantos?\s+contadores?\b/i,
      /\bcuantos?\s+(?:hay\s+)?(?:del?\s+)?modelo\b/i,
      /\bbusqueda\s+(?:global\s+)?(?:por\s+)?modelo\b/i,
      /\bcontadores?\s+(?:del?\s+|por\s+)?modelo\b/i,
      /\bmodelo\s+(?:de\s+)?contador(?:es?)?\b/i,
      /\bhow\s+many\s+meters?\s+(?:are|exist|have|of\s+model)\b/i,
      /\bsearch\s+(?:meters?\s+)?by\s+model\b/i,
      /\bmodel\s+(?:search|lookup)\b/i,
      /\brecherche\s+(?:compteurs?\s+)?par\s+modele\b/i,
      /\bricerca\s+(?:contatori\s+)?per\s+modello\b/i,
      // Catches "contadores/medidores {brand-name}" without explicit "cuantos/modelo/busqueda".
      // The negative lookahead excludes structural device-list words so "contadores asignados",
      // "contadores listado", etc. do NOT trigger model search. Dispatch treats an implicit
      // name as the manufacturer term, not a context-switch target.
      /\b(?:contadores?|medidores?)\s+(?!(?:asignados?|no\s+asignados?|sin\s+(?:asignar|comunicacion|cups)|desactivados?|del?\s+cliente|en\s+red|listado|activos?|instalados?|revision|sustituciones?|pendientes?|bateria|gprs|walk.by|internos?|mbus|zona\b|agrupaci[oó]n|entidad|de\s+la|del?\s+|los\b|las\b|en\b)\b)([a-z]{4,})(?:\s+existentes?)?\b/,
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/busqueda-por-modelo",
    response: "chatbot.navigate-mantenimiento",
    description:
      "Navega a la búsqueda global de modelos de contador (cuántos hay por fabricante/modelo y entidad/agrupación). Si se menciona una entidad o agrupación, cambia el contexto automáticamente."
  },

  // ── Navigation: Maintenance ────────────────────────────────────────────────
  {
    id: "navigate-mantenimiento",
    patterns: [
      /\b(mantenimiento|manteniment|manutencao|reparaciones?|correctivo|preventivo|estado\s+del\s+sistema)\b/,
      /\b(maintenance|repairs?|corrective|preventive)\b/,
      /\b(maintenance|reparations?|correctif|preventif)\b/,
      /\b(manutenzione|riparazioni|correttivo|preventivo)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "mantenimiento/global/gateways",
    response: "chatbot.navigate-mantenimiento",
    description:
      "Navega a la sección de mantenimiento global. Incluye sub-secciones: activaciones, estado de comunicaciones, alarmas globales, incidencias, cola de comandos, LNS, y más."
  },

  // ── Navigation: Telecontrol subsections ─────────────────────────────────────

  // Telecontrol: historical
  {
    id: "navigate-telecontrol-historico",
    patterns: [
      /\b(historico\s+(?:de\s+)?telecontrol|telecontrol\s+historico|historial\s+telecontrol)\b/,
      /\bhistori(?:al|co)\s+(?:de\s+)?acciones\s+remotas\b/,
      /\b(telecontrol\s+history|history\s+telecontrol|telecontrol\s+historical)\b/,
      /\b(historique\s+telecommande|storico\s+telecontrollo)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "telecontrol/historico",
    response: "chatbot.navigate-telecontrol"
  },

  // Telecontrol: network schema
  {
    id: "navigate-telecontrol-esquema",
    patterns: [
      /\b(esquema\s+(?:de\s+)?red\s+telecontrol|topologia\s+(?:de\s+)?(?:red\s+)?telecontrol|telecontrol\s+esquema)\b/,
      /\b(telecontrol\s+network\s+(?:schema|diagram|topology)|network\s+schema\s+telecontrol)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "telecontrol/esquema-red",
    response: "chatbot.navigate-telecontrol"
  },

  // Telecontrol: telemetering
  {
    id: "navigate-telecontrol-telemedida",
    patterns: [
      /\b(telemedida|telemedicion|telemetraje|telemetria(?:\s+remota)?|telecontrol\s+telemedida)\b/,
      /\b(telemetering|telemetry\s+telecontrol|remote\s+metering)\b/,
      /\b(telereleve|telemetria\s+telecontrollo)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "telecontrol/telemedida",
    response: "chatbot.navigate-telecontrol"
  },

  // Telecontrol: dataloggers
  {
    id: "navigate-telecontrol-dataloggers",
    patterns: [
      /\b(dataloggers?|registradores?\s+(?:de\s+)?datos?|telecontrol\s+dataloggers?)\b/,
      /\b(data\s+loggers?|datalogger\s+devices?|telecontrol\s+data\s+loggers?)\b/,
      /\b(enregistreurs?\s+donnees?|registratori\s+dati)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "telecontrol/dataloggers",
    response: "chatbot.navigate-telecontrol"
  },

  // Telecontrol: test
  {
    id: "navigate-telecontrol-test",
    patterns: [
      /\b(test\s+(?:de\s+)?telecontrol|prueba\s+(?:de\s+)?telecontrol|telecontrol\s+(?:de\s+)?prueba|probar\s+telecontrol)\b/,
      /\b(telecontrol\s+test|test\s+(?:the\s+)?telecontrol|testing\s+telecontrol)\b/,
      /\b(test\s+telecommande|test\s+telecontrollo)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "telecontrol/test",
    response: "chatbot.navigate-telecontrol"
  },

  // ── Navigation: Telecontrol ──────────────────────────────────────────────────
  {
    id: "navigate-telecontrol",
    patterns: [
      /\b(telecontrol|control\s+remoto|telecontrollo|telecontrolo|controle\s+remoto)\b/,
      /\b(valvulas?|cortes?|aperturas?|actuaciones?|alertas?\s+activas?)\b/,
      /\b(remote\s+control|valves?|cuts?|active\s+alerts?)\b/,
      /\b(telecommande|vannes?|coupures?|alertes?\s+actives?)\b/,
      /\b(telecontrollo|valvole|interruzioni)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "telecontrol/alertas-activas",
    response: "chatbot.navigate-telecontrol"
  },

  // ── Navigation: Files subsections ───────────────────────────────────────────

  // Files: templates
  {
    id: "navigate-ficheros-plantillas",
    patterns: [
      /\bplantillas?\s+(?:de\s+)?ficheros?\b/,
      /\beditar\s+plantillas?\b/,
      /\bficheros?\s+(?:de\s+)?plantillas?\b/,
      /\bplantillas?\s+archivos?\b/,
      /\b(file\s+templates?|templates?\s+files?|output\s+templates?)\b/,
      /\b(modeles?\s+fichiers?|template\s+file|modelli\s+file)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "ficheros/plantillas/listado",
    response: "chatbot.navigate-ficheros"
  },

  // Files: schedules
  {
    id: "navigate-ficheros-horarios",
    patterns: [
      /\bhorarios?\s+(?:de\s+)?ficheros?\b/,
      /\bficheros?\s+(?:de\s+)?horarios?\b/,
      /\bficheros?\s+programados?\b/,
      /\b(file\s+schedules?|scheduled\s+files?|files?\s+schedules?)\b/,
      /\b(horaires?\s+fichiers?|orari\s+file)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "ficheros/horarios",
    response: "chatbot.navigate-ficheros"
  },

  // Files: EK280
  {
    id: "navigate-ficheros-ek280",
    patterns: [
      /\b(ek\s*280|ficheros?\s+ek280|ek280\s+ficheros?)\b/,
      /\b(ek280\s+files?|files?\s+ek280)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "ficheros/ek280",
    response: "chatbot.navigate-ficheros"
  },

  // Files: Aqualia
  {
    id: "navigate-ficheros-aqualia",
    patterns: [
      /\b(aqualia\s+ficheros?|ficheros?\s+aqualia)\b/,
      /\b(aqualia\s+files?|files?\s+aqualia)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "ficheros/aqualia",
    response: "chatbot.navigate-ficheros"
  },

  // ── Navigation: Files ─────────────────────────────────────────────────────
  {
    id: "navigate-ficheros",
    patterns: [
      /\b(ficheros?|arxius?|archivos?|documentos?\s+de\s+salida)\b/,
      /\b(output\s+files?|export\s+files?)\b/,
      /\b(fichiers?\s+de\s+sortie|fichiers?\s+generados?)\b/,
      /\b(file\s+di\s+output|archivi?\s+generati?)\b/,
      /\b(ficheiros?\s+de\s+saida)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "ficheros/descarga",
    response: "chatbot.navigate-ficheros"
  },

  // ── Navigation: Data management subsections ─────────────────────────────────

  // Data management: installed CUPS list
  {
    id: "navigate-gestion-datos-cups-instalados",
    patterns: [
      /\b(cups\s+instalados?|listado\s+cups\s+instalados?|suministros?\s+instalados?)\b/,
      /\b(installed\s+cups|cups\s+installed|installed\s+supply\s+points?)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/cups/listado-instalados",
    response: "chatbot.navigate-gestion-datos"
  },

  // Data management: devices without CUPS
  {
    id: "navigate-gestion-datos-sin-cups",
    patterns: [
      /\b(?:(?:dispositivos?|medidores?|contadores?)\s+)?sin\s+(?:cups|suministro)\b/,
      /\bdevices?\s+without\s+cups\b/,
      /\bno\s+cups\s+devices?\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/cups/dispositivos-sin-cups",
    response: "chatbot.navigate-gestion-datos"
  },

  // Data management: configuration
  {
    id: "navigate-gestion-datos-configuracion",
    patterns: [
      /\bconfiguracion\s+(?:de\s+)?gestion\s+(?:de\s+)?datos?\b/,
      /\bgestion\s+datos?\s+configuracion\b/,
      /\b(?:opciones|ajustes)\s+(?:de\s+)?gestion\s+(?:de\s+)?datos?\b/,
      /\b(data\s+management\s+(?:configuration|settings?)|configure\s+data\s+management)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/configuracion",
    response: "chatbot.navigate-gestion-datos"
  },

  // Data management: metrology
  {
    id: "navigate-gestion-datos-metrologia",
    patterns: [
      /\b(metrologia|configuracion\s+metrologia|metrologia\s+configuracion)\b/,
      /\b(metrology|metrology\s+(?:configuration|settings?)|configure\s+metrology)\b/,
      /\b(metrologie|metrologia\s+configurazione)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/metrologia/configuracion",
    response: "chatbot.navigate-gestion-datos"
  },

  // Data management: import
  {
    id: "navigate-gestion-datos-importacion",
    patterns: [
      /\b(importacion\s+(?:de\s+)?datos?(?:\s+gestion)?|importar\s+datos?\s+(?:de\s+)?gestion|gestion\s+datos?\s+importacion)\b/,
      /\b(data\s+management\s+import|import\s+data\s+management)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/importar/datos",
    response: "chatbot.navigate-gestion-datos"
  },

  // Data management: PTA
  {
    id: "navigate-gestion-datos-pta",
    patterns: [
      /\b(pta|punto\s+(?:de\s+)?tarifa|gestion\s+datos?\s+pta)\b/,
      /\b(pta\s+(?:management|data)|tariff\s+point)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/pta",
    response: "chatbot.navigate-gestion-datos"
  },

  // ── Navigation: Data management ────────────────────────────────────────────
  {
    id: "navigate-gestion-datos",
    patterns: [
      /\b(gestion\s+de\s+datos?|gestion\s+datos?|gestio\s+dades?)\b/,
      /\bimportar\s*(?:\/|y|o)?\s*exportar\b/,
      /\b(importar|exportar|importacion|exportacion|volcados?)\b/,
      /\b(data\s+management|import|export)\b/,
      /\b(gestion\s+des\s+donnees|importer|exporter)\b/,
      /\b(gestione\s+dati|importare|esportare)\b/,
      /\b(gestao\s+de\s+dados|importar|exportar)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/informacion",
    response: "chatbot.navigate-gestion-datos"
  },

  // ── Navigation: Network status subsections ──────────────────────────────────

  // Network status: entity
  {
    id: "navigate-estado-red-entidad",
    patterns: [
      /\bestado\s+(?:de\s+)?red\s+entidad\b/,
      /\bestado\s+red\s+(?:de\s+la\s+)?entidad\b/,
      /\bred\s+de\s+la\s+entidad\b/,
      /\b(network\s+status\s+entity|entity\s+network\s+(?:status|state))\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "estado-red/entidad",
    response: "chatbot.navigate-estado-red"
  },

  // Network status: KPIs
  {
    id: "navigate-estado-red-kpis",
    patterns: [
      /\bkpis?\s+(?:de\s+)?red\b/,
      /\bindicadores?\s+(?:de\s+)?(?:rendimiento\s+(?:de\s+)?(?:la\s+)?)?red\b/,
      /\bnetwork\s+kpis?\b/,
      /\bkpis?\s+network\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "estado-red/kpis",
    response: "chatbot.navigate-estado-red"
  },

  // Network status: global
  {
    id: "navigate-estado-red-global",
    patterns: [
      /\bestado\s+(?:de\s+)?red\s+global\b/,
      /\bestado\s+global\s+(?:de\s+la\s+)?red\b/,
      /\bred\s+global\b/,
      /\b(global\s+network\s+(?:status|state)|network\s+(?:status\s+)?global)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "estado-red/global",
    response: "chatbot.navigate-estado-red"
  },

  // Network status: global summary
  {
    id: "navigate-estado-red-resumen-global",
    patterns: [
      /\bresumen\s+global\s+(?:de\s+la\s+)?red\b/,
      /\bresumen\s+estado\s+(?:de\s+la\s+)?red\b/,
      /\b(network\s+(?:global\s+)?summary|global\s+network\s+summary)\b/,
      /\b(resume\s+(?:global\s+)?reseau|riepilogo\s+globale\s+rete)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "estado-red/resumen-global",
    response: "chatbot.navigate-estado-red"
  },

  // ── Navigation: Network status ────────────────────────────────────────────────
  {
    id: "navigate-estado-red",
    patterns: [
      /\b(estado\s+(?:de\s+(?:la\s+)?)?red(?:\s+lora)?|estat\s+de\s+xarxa)\b/,
      /\b(network\s+state|network\s+status|network\s+health)\b/,
      /\b(etat\s+du\s+reseau|etat\s+reseau)\b/,
      /\b(stato\s+della\s+rete|stato\s+rete)\b/,
      /\b(estado\s+da\s+rede)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "estado-red/global",
    response: "chatbot.navigate-estado-red"
  },

  // Navigation: Entities
  {
    id: "navigate-entidades",
    patterns: [
      /\b(entidades?|entitats?|organizaciones?|organismos?)\b/,
      /\b(entities|organizations|organisations)\b/,
      /\b(entites|organisations|organismes)\b/,
      /\b(entita|organizzazioni|organismi)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "entidades/listado",
    response: "chatbot.navigate-entidades"
  },

  // Navigation: CUPS
  {
    id: "navigate-cups",
    patterns: [
      /\b(cups|puntos?\s+de\s+suministro|suministros?)\b/,
      /\b(supply\s+points?|metering\s+points?)\b/,
      /\b(points?\s+de\s+fourniture|points?\s+de\s+livraison)\b/,
      /\b(punti\s+di\s+fornitura)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "gestion-datos/cups/listado",
    response: "chatbot.navigate-cups"
  },

  // Navigation: Agrupations
  {
    id: "navigate-agrupaciones",
    patterns: [
      /\b(listado\s+de\s+agrupaciones?|agrupaciones?\s+listado|listado\s+agrupaciones?|todas\s+las\s+agrupaciones)\b/,
      /\b(agrupations?\s+list|list\s+of\s+agrupations?|agrupations?\s+listing)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "agrupaciones/listado",
    response: "chatbot.navigate-agrupaciones"
  },

  // ── Navigation: Sensors subsections ─────────────────────────────────────────

  // Sensors: pressure
  {
    id: "navigate-sensorica-presion",
    patterns: [
      /\b(sensores?\s+(?:de\s+)?presion|presion\s+sensorica|sensorica\s+presion)\b/,
      /\b(pressure\s+sensors?|sensors?\s+pressure|sensorics?\s+pressure)\b/,
      /\b(capteurs?\s+pression|sensori\s+pressione)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "sensorica/presion",
    response: "chatbot.navigate-sensorica"
  },

  // Sensors: flow rate
  {
    id: "navigate-sensorica-caudal",
    patterns: [
      /\b(sensores?\s+(?:de\s+)?caudal|caudal\s+sensorica|sensorica\s+caudal|medicion\s+caudal)\b/,
      /\bflow\s+(?:rate\s+)?sensors?\b/,
      /\bsensors?\s+flow\s+rate\b/,
      /\b(capteurs?\s+(?:de\s+)?debit|sensori\s+portata)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "sensorica/caudal",
    response: "chatbot.navigate-sensorica"
  },

  // ── Navigation: Sensors ────────────────────────────────────────────────────
  {
    id: "navigate-sensorica",
    patterns: [
      /\b(sensorica|sensores|sensores?\s+de\s+fugas?|medicion\s+ambiental)\b/,
      /\b(sensorics?|leak\s+sensors?|pressure\s+sensors?|environmental\s+measurement)\b/,
      /\b(sensorique|capteurs?\s+fuites?|capteurs?\s+pression)\b/,
      /\b(sensoristica|sensori\s+perdite|sensori\s+pressione)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "sensorica/fugas",
    response: "chatbot.navigate-sensorica"
  },

  // ── Navigation: Work orders ──────────────────────────────────────────
  {
    id: "navigate-ordenes-rutas",
    patterns: [
      /\brutas?\s+(?:de\s+trabajo|ordenes?)\b/,
      /\bordenes?\s+(?:de\s+)?rutas?\b/,
      /\bgestion\s+(?:de\s+)?rutas?\b/,
      /\b(route\s+orders?|work\s+routes?|routes?\s+(?:management|orders?))\b/,
      /\b(routes?\s+(?:de\s+)?travail|rotte\s+(?:di\s+)?lavoro)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "ordenes/rutas",
    response: "chatbot.navigate-ordenes"
  },

  {
    id: "navigate-ordenes",
    patterns: [
      /\b(ordenes?\s+de\s+trabajo|ordres?\s+de\s+treball|ots?)\b/,
      /\b(work\s+orders?|job\s+orders?|task\s+orders?)\b/,
      /\b(ordres?\s+de\s+travail|bons?\s+de\s+travail)\b/,
      /\b(ordini\s+di\s+lavoro|commesse)\b/,
      /\b(ordens?\s+de\s+trabalho)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "ordenes/listado",
    response: "chatbot.navigate-ordenes"
  },

  // ── Navigation: Installer subsections ───────────────────────────────────────

  // Installer: history
  {
    id: "navigate-instalador-historico",
    patterns: [
      /\b(historico\s+(?:del?\s+)?instalador|instalador\s+historico|histori(?:al|co)\s+(?:de\s+)?instalaciones?|instalaciones\s+anteriores)\b/,
      /\b(installer\s+(?:history|historical)|installation\s+history|historical\s+installations?)\b/,
      /\b(historique\s+installation|storico\s+installazioni)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "instalador/historico",
    response: "chatbot.navigate-instalador"
  },

  // ── Navigation: Installer ───────────────────────────────────────────────────
  {
    id: "navigate-instalador",
    patterns: [
      /\b(instalador|instalacion|activacion|activar\s+(?:medidor|contador|dispositivo)|nuevas?\s+instalaciones?)\b/,
      /\b(installer|installation|activation|activate\s+meter|new\s+installations?)\b/,
      /\b(installateur|installation\s+de\s+compteur|activation)\b/,
      /\b(installatore|installazione|attivazione)\b/,
      /\b(instalador|instalacao|ativacao)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "instalador/activacion",
    response: "chatbot.navigate-instalador"
  },

  // ── Navigation: Subscriber profile ────────────────────────────────

  // Subscriber: configuration
  {
    id: "navigate-abonado-configuracion",
    patterns: [
      /\b(configuracion\s+(?:del?\s+)?abonado|abonado\s+configuracion|ajustes?\s+abonado)\b/,
      /\b(subscriber\s+(?:configuration|settings?)|configure\s+subscriber)\b/,
      /\b(configuration\s+abonne|impostazioni\s+abbonato)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "abonado/configuracion",
    response: "chatbot.navigate-home"
  },

  // Subscriber: main panel
  {
    id: "navigate-abonado",
    patterns: [
      /\b(abonado|panel\s+abonado|mi\s+consumo|mis?\s+lecturas?\s+abonado)\b/,
      /\b(subscriber|subscriber\s+panel|my\s+consumption\s+panel|end\s+consumer)\b/,
      /\b(abonne|consommateur|abbonato|assinante)\b/
    ],
    action: ChatAction.NAVIGATE,
    payload: "abonado/principal",
    response: "chatbot.navigate-home"
  }
];
